// ════════════════════════════════════════════════════════════════════
// Glass-i — one Customer Price List, as its own page.
//
// Route: /erp/masters/price-lists/:id
//
// WHY A PAGE AND NOT A DRAWER
//   This is not a quick edit. A card carries five rate grids, an assignment
//   list and an audit history, and a negotiation is entered over a sitting —
//   which is a workspace, not an overlay. As a route it gains a URL that can
//   be sent to someone, browser back, and the full page width for the grids.
//   The one thing the drawer did better was keep Save always in reach, so the
//   action bar here is sticky.
//
// GATING — every WRITE is master-only server-side (decision D7). This page
// hides the write controls from a non-master as a courtesy; the gate that
// matters is `requireMaster` on the route.
// ════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, App, Button, Card, DatePicker, Empty, Form, Input,
  InputNumber, Modal, Popconfirm, Select, Space, Table, Tabs, Tag, Tooltip, Typography
} from 'antd';
import {
  ArrowLeftOutlined, PlusOutlined, DeleteOutlined,
  CheckCircleOutlined, LockOutlined, HistoryOutlined, TeamOutlined,
  DownloadOutlined, UploadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PriceListRateGrid from './PriceListRateGrid';
import useAuth from '../../../../hooks/useAuth';
import priceListApi, { saveBlob } from './priceListApi';
import { customerApi } from '../customers/customerApi';
import {
  DATE_FMT, KINDS, statusTheme, StatusDot, Fact, money,
  toApiDate, fromApiDate, isExpired, pager, errMessage, PAGE_CSS
} from './priceListShared';

const { Text } = Typography;

// ════════════════════════════════════════════════════════════════════
// One rate tab. Identical for all five kinds — only the master catalogue
// behind it differs.
// ════════════════════════════════════════════════════════════════════
function RateTab({ kind, options, rates, onChange, readOnly, onSearchItems }) {
  const byRef = useMemo(() => {
    const m = new Map();
    for (const o of options || []) m.set(o.id, o);
    return m;
  }, [options]);

  const rows = useMemo(() => (rates || []).filter((r) => r.rateKind === kind.key), [rates, kind.key]);

  // Index mapping: the grid shows ONE kind, but `rates` holds all five mixed.
  // Every mutation therefore translates a filtered index back to its real slot.
  const realIndex = (idx) => {
    const targets = (rates || []).map((r, i) => ({ r, i })).filter((x) => x.r.rateKind === kind.key);
    return targets[idx] ? targets[idx].i : -1;
  };

  const patchRow = (idx, patch) => {
    const real = realIndex(idx);
    if (real < 0) return;
    const next = [...(rates || [])];
    next[real] = { ...next[real], ...patch, rateKind: kind.key };
    onChange(next);
  };
  const removeRow = (idx) => {
    const real = realIndex(idx);
    if (real < 0) return;
    onChange((rates || []).filter((_r, i) => i !== real));
  };
  const blank = () => ({
    rateKind: kind.key, refId: null, thicknessMm: null,
    rate: null, priceUomCode: null, minRate: null, maxRate: null, note: null
  });
  /** `at` is a FILTERED position; appending uses the end of the whole array. */
  const insertRow = (at) => {
    const next = [...(rates || [])];
    const real = at >= rows.length ? next.length : realIndex(at);
    next.splice(real < 0 ? next.length : real, 0, blank());
    onChange(next);
  };
  const duplicateRow = (idx) => {
    const real = realIndex(idx);
    if (real < 0) return;
    const next = [...(rates || [])];
    // The ref is cleared: the same master row cannot be priced twice on one
    // card (0181's partial unique index), so a straight copy could only ever
    // be rejected on save. Everything else carries, which is the point.
    next.splice(real + 1, 0, { ...next[real], refId: null });
    onChange(next);
  };

  // Below-band warning: the item master's own min/max is a discount clamp, and
  // a signed contract rate is the legitimate exception — so this warns, it
  // never blocks (see the plan, §7).
  const belowBand = rows.filter((r) => {
    const opt = byRef.get(r.refId);
    return opt && opt.minRate != null && r.rate != null && Number(r.rate) < Number(opt.minRate);
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {kind.key === 'OTHER_CHARGE' && (
        <Alert
          type="info"
          showIcon
          message="A price list can move a charge's value, never how it is calculated."
          description="Slab and multiplier rules hold more than one number, so they cannot be negotiated per customer. A rule the Other Charges Master has locked is greyed out here — no price list can move it."
        />
      )}
      {belowBand.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${belowBand.length} rate(s) are below the item master's minimum.`}
          description="That is allowed — the min/max band exists to clamp ad-hoc discounts, and a negotiated contract rate is the legitimate exception. Shown so it is a decision, not an accident."
        />
      )}
      <PriceListRateGrid
        kindKey={kind.key}
        options={options}
        rows={rows}
        readOnly={readOnly}
        onSearchItems={onSearchItems}
        onPatchRow={patchRow}
        onRemoveRow={removeRow}
        onInsertRow={insertRow}
        onDuplicateRow={duplicateRow}
      />
    </Space>
  );
}

// ════════════════════════════════════════════════════════════════════
// Assignments tab
// ════════════════════════════════════════════════════════════════════
function AssignmentsTab({ listId, assignments, readOnly, onChanged }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [scope, setScope] = useState('PRICE_GROUP');

  const searchCustomers = useCallback(async (q) => {
    try {
      const res = await customerApi.list({ search: q, limit: 25 });
      const rows = (res && res.rows) || (Array.isArray(res) ? res : []);
      setCustomers(rows);
    } catch (_e) { setCustomers([]); }
  }, []);

  useEffect(() => { searchCustomers(''); }, [searchCustomers]);

  const add = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await priceListApi.addAssignment(listId, {
        scope: v.scope,
        customerId: v.scope === 'CUSTOMER' ? v.customerId : null,
        priceGroup: v.scope === 'PRICE_GROUP' ? v.priceGroup : null,
        effectiveFrom: toApiDate(v.effectiveFrom),
        effectiveTo: toApiDate(v.effectiveTo)
      });
      form.resetFields();
      message.success('Assigned.');
      onChanged();
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(errMessage(e, 'Could not assign.'));
    } finally { setSaving(false); }
  };

  const remove = async (assignmentId) => {
    try {
      await priceListApi.removeAssignment(listId, assignmentId);
      message.success('Removed.');
      onChanged();
    } catch (e) { message.error(errMessage(e, 'Could not remove.')); }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="A group covers everyone carrying that label; a named customer beats the group, row by row."
        description="Price group matches customers.price_group and is compared ignoring case and surrounding spaces, so “Dealer”, “dealer” and “Dealer ” are one group."
      />
      <Table
        size="small"
        rowKey="id"
        dataSource={assignments || []}
        pagination={pager(10, { hideOnSinglePage: true })}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Not assigned to anyone yet — this list prices nothing." /> }}
        columns={[
          {
            title: 'Scope',
            dataIndex: 'scope',
            width: 130,
            render: (v) => <Tag color={v === 'CUSTOMER' ? 'blue' : 'purple'}>{v === 'CUSTOMER' ? 'Customer' : 'Group'}</Tag>
          },
          {
            title: 'Applies to',
            key: 'target',
            render: (_v, r) => (r.scope === 'CUSTOMER'
              ? (
                <span>
                  {r.customerName || <Text type="secondary">(customer removed)</Text>}
                  {r.customerPriceGroup ? <Text type="secondary"> · group: {r.customerPriceGroup}</Text> : null}
                </span>
              )
              : r.priceGroup)
          },
          {
            title: 'Effective',
            key: 'window',
            width: 220,
            render: (_v, r) => (
              <Text type="secondary">
                {r.effectiveFrom ? dayjs(r.effectiveFrom, API_DATE).format(DATE_FMT) : 'always'}
                {' → '}
                {r.effectiveTo ? dayjs(r.effectiveTo, API_DATE).format(DATE_FMT) : 'open'}
              </Text>
            )
          },
          ...(readOnly ? [] : [{
            title: '',
            key: 'rm',
            width: 60,
            align: 'center',
            render: (_v, r) => (
              <Popconfirm title="Remove this assignment?" onConfirm={() => remove(r.id)}>
                <Button type="text" danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            )
          }])
        ]}
      />
      {!readOnly && (
        <Card size="small" title="Assign">
          <Form form={form} layout="inline" initialValues={{ scope: 'PRICE_GROUP' }}>
            <Form.Item name="scope" rules={[{ required: true }]}>
              <Select
                style={{ width: 150 }}
                onChange={setScope}
                options={[
                  { value: 'PRICE_GROUP', label: 'Price group' },
                  { value: 'CUSTOMER', label: 'Customer' }
                ]}
              />
            </Form.Item>
            {scope === 'CUSTOMER' ? (
              <Form.Item name="customerId" rules={[{ required: true, message: 'Pick a customer.' }]}>
                <Select
                  showSearch
                  filterOption={false}
                  onSearch={searchCustomers}
                  placeholder="Search customers…"
                  style={{ width: 280 }}
                  options={customers.map((c) => ({ value: c.id, label: c.customerName || c.displayName }))}
                />
              </Form.Item>
            ) : (
              <Form.Item name="priceGroup" rules={[{ required: true, message: 'Enter a price group.' }]}>
                <Input placeholder="e.g. Dealer" style={{ width: 220 }} />
              </Form.Item>
            )}
            <Form.Item name="effectiveFrom"><DatePicker format={DATE_FMT} placeholder="From" /></Form.Item>
            <Form.Item name="effectiveTo"><DatePicker format={DATE_FMT} placeholder="To" /></Form.Item>
            <Form.Item>
              <Button type="primary" loading={saving} onClick={add} icon={<TeamOutlined />}>Add</Button>
            </Form.Item>
          </Form>
        </Card>
      )}
    </Space>
  );
}

// ════════════════════════════════════════════════════════════════════
// The editor drawer
// ════════════════════════════════════════════════════════════════════
export default function PriceListDetailPage() {
  const { id: listId } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const isMaster = me?.isMaster === true;
  const backToList = useCallback(() => navigate('/erp/masters/price-lists'), [navigate]);
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rates, setRates] = useState([]);
  const [options, setOptions] = useState({});
  const [events, setEvents] = useState([]);
  const [form] = Form.useForm();

  const readOnly = !isMaster || (detail && detail.status === 'ARCHIVED');

  const load = useCallback(async () => {
    if (!listId) return;
    setLoading(true);
    try {
      const [d, o] = await Promise.all([
        priceListApi.get(listId),
        priceListApi.masterOptions('')
      ]);
      setDetail(d);
      setRates(d.rates || []);
      setOptions(o || {});
      form.setFieldsValue({
        listName: d.listName,
        description: d.description,
        priority: d.priority,
        effectiveFrom: fromApiDate(d.effectiveFrom),
        effectiveTo: fromApiDate(d.effectiveTo)
      });
    } catch (e) {
      message.error(errMessage(e, 'Could not load the price list.'));
    } finally { setLoading(false); }
  }, [listId, form, message]);

  useEffect(() => { load(); }, [load]);

  const searchItems = useCallback(async (q) => {
    try {
      const o = await priceListApi.masterOptions(q || '');
      setOptions((prev) => ({ ...prev, ITEM: (o && o.ITEM) || [] }));
    } catch (_e) { /* keep the previous options rather than emptying the picker */ }
  }, []);

  const loadEvents = useCallback(async () => {
    try { setEvents(await priceListApi.events(listId)); } catch (_e) { setEvents([]); }
  }, [listId]);

  const saveAll = async () => {
    setSaving(true);
    try {
      const v = await form.validateFields();
      await priceListApi.update(listId, {
        listName: v.listName,
        description: v.description || null,
        priority: v.priority ?? 0,
        effectiveFrom: toApiDate(v.effectiveFrom),
        effectiveTo: toApiDate(v.effectiveTo)
      });
      // The whole set is posted — a row removed above is removed here. Rows
      // still missing a master row or a rate are dropped rather than rejected,
      // so a half-typed row never blocks the save of the rest.
      const clean = rates
        .filter((r) => r.refId && r.rate !== null && r.rate !== undefined && r.priceUomCode)
        .map((r) => ({
          rateKind: r.rateKind,
          refId: r.refId,
          thicknessMm: r.rateKind === 'PROCESS' ? (r.thicknessMm ?? null) : null,
          rate: Number(r.rate),
          priceUomCode: r.priceUomCode,
          minRate: r.rateKind === 'ITEM' ? (r.minRate ?? null) : null,
          maxRate: r.rateKind === 'ITEM' ? (r.maxRate ?? null) : null,
          note: r.note || null
        }));
      const dropped = rates.length - clean.length;
      const res = await priceListApi.saveRates(listId, clean);
      message.success(
        `Saved — ${res.added} added, ${res.changed} changed, ${res.removed} removed.`
        + (dropped ? ` ${dropped} incomplete row(s) were not saved.` : ''));
      await load();
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(errMessage(e, 'Could not save.'));
    } finally { setSaving(false); }
  };

  // ── Stage 6PL-1e — CSV export / import ────────────────────────────
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const exportCsv = async () => {
    try {
      const resp = await priceListApi.exportRatesCsv(listId);
      saveBlob(resp.data, `${(detail && detail.listCode) || 'price-list'}-rates.csv`);
    } catch (e) { message.error(errMessage(e, 'Could not export.')); }
  };

  const pickImportFile = () => { if (fileRef.current) fileRef.current.click(); };

  const onImportFile = async (ev) => {
    const file = ev.target.files && ev.target.files[0];
    ev.target.value = '';                     // so re-picking the same file fires again
    if (!file) return;
    let text;
    try { text = await file.text(); }
    catch (_e) { message.error('Could not read that file.'); return; }

    setImporting(true);
    try {
      // Parse first, WITHOUT writing. Two reasons: the confirmation can state
      // the real row count, and a malformed file is rejected before the
      // operator has agreed to replace anything.
      const dry = await priceListApi.importRatesCsv(listId, text, true);
      const existing = (detail && Array.isArray(detail.rates)) ? detail.rates.length : 0;
      Modal.confirm({
        title: 'Replace every rate on this card?',
        width: 520,
        okText: `Replace with ${dry.parsed} row${dry.parsed === 1 ? '' : 's'}`,
        okButtonProps: { danger: true },
        content: (
          <div style={{ fontSize: 13 }}>
            <p style={{ marginTop: 0 }}>
              <strong>{file.name}</strong> holds <strong>{dry.parsed}</strong> rate
              {dry.parsed === 1 ? '' : 's'}. This card currently has <strong>{existing}</strong>.
            </p>
            <p style={{ marginBottom: 0 }}>
              Importing <strong>replaces the whole set</strong> — it does not merge. Any rate on
              the card but not in the file is removed, which is what lets you delete a row by
              deleting it from the spreadsheet. Documents already saved keep their rates either way.
            </p>
          </div>
        ),
        onOk: async () => {
          try {
            const r = await priceListApi.importRatesCsv(listId, text, false);
            message.success(`Imported: ${r.added} added, ${r.changed} changed, ${r.removed} removed.`);
            await load();
            
          } catch (e) { message.error(errMessage(e, 'Could not import.')); }
        }
      });
    } catch (e) {
      // A 422 lists every bad row with its line number — showing only the first
      // would mean fixing the file one round trip at a time.
      const details = e && e.response && e.response.data && e.response.data.error
        && e.response.data.error.details;
      const errors = (details && Array.isArray(details.errors)) ? details.errors : null;
      if (errors) {
        Modal.error({
          title: `Nothing was imported — ${errors.length} problem${errors.length === 1 ? '' : 's'} in the file`,
          width: 560,
          content: (
            <div style={{ fontSize: 13, maxHeight: 320, overflow: 'auto' }}>
              {errors.map((x, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  <strong>Line {x.line}:</strong> {x.message}
                </div>
              ))}
            </div>
          )
        });
      } else {
        message.error(errMessage(e, 'Could not read that file.'));
      }
    } finally { setImporting(false); }
  };

  const activate = async () => {
    try {
      await priceListApi.activate(listId);
      message.success('This price list is now live for the customers assigned to it.');
      await load();
    } catch (e) { message.error(errMessage(e, 'Could not activate.')); }
  };

  const counts = useMemo(() => {
    const m = {};
    for (const r of rates) m[r.rateKind] = (m[r.rateKind] || 0) + 1;
    return m;
  }, [rates]);

  return (
    <div className="pl-page">
      <style>{PAGE_CSS}</style>

      {/* ── Page chrome ─────────────────────────────────────────────
          Was a 1180px Drawer. As a route it gets a real back affordance,
          a URL you can send someone, and browser history — all of which a
          drawer swallowed. The action bar is STICKY so Save stays reachable
          from the bottom of a 200-row rate grid; in the drawer it was pinned
          in the header and that was the one thing the drawer did better. */}
      <div className="pl-actionbar">
        <Space size={10} align="center" wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={backToList}>Price lists</Button>
          {detail ? (
            <>
              <StatusDot status={detail.status} />
              <span style={{ fontWeight: 700, fontSize: 17, letterSpacing: '.01em' }}>
                {detail.listCode}
              </span>
              <Tag color={statusTheme(detail.status).color} style={{ marginInlineEnd: 0 }}>
                {statusTheme(detail.status).text}
              </Tag>
              {isExpired(detail) && <Tag color="red" style={{ marginInlineEnd: 0 }}>Expired</Tag>}
              {!isMaster && (
                <Tag icon={<LockOutlined />} style={{ marginInlineEnd: 0 }}>Read only</Tag>
              )}
            </>
          ) : <span style={{ fontWeight: 600 }}>Price list</span>}
        </Space>
        {detail ? (
          <Space style={{ marginLeft: 'auto' }}>
          {/* Export is a READ — a salesperson who may see the card may take a
              copy of it, including from an archived one (that is often exactly
              the card someone needs to produce). */}
          <Tooltip title="Download this card's rates as a CSV you can open in Excel">
            <Button icon={<DownloadOutlined />} onClick={exportCsv}>Export</Button>
          </Tooltip>
          {isMaster && detail.status !== 'ARCHIVED' ? (
            <>
              <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
                onChange={onImportFile} />
              <Tooltip title="Replace every rate on this card from a CSV">
                <Button icon={<UploadOutlined />} loading={importing} onClick={pickImportFile}>Import</Button>
              </Tooltip>
            </>
          ) : null}
          {isMaster && detail.status !== 'ARCHIVED' && detail.status === 'DRAFT' && (
            <Popconfirm
              title="Make this price list live?"
              description="From now on it prices new documents for the customers assigned to it. Documents already saved are untouched."
              onConfirm={activate}
            >
              {/* Green, not another blue. Activating is the one action here
                  that changes what customers are charged, and it read as
                  interchangeable with Export when every button was grey. */}
              <Button className="pl-activate" icon={<CheckCircleOutlined />}>Activate</Button>
            </Popconfirm>
          )}
          {isMaster && detail.status !== 'ARCHIVED'
            ? <Button type="primary" loading={saving} onClick={saveAll}>Save</Button>
            : null}
          </Space>
        ) : null}
      </div>

      <div className="pl-body">
      {!isMaster && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="You can read this price list but not change it."
          description="Changing a price affects every future quotation for every customer on this card, so it is reserved to the master account."
        />
      )}
      {/* No separate "this is archived" Alert: the status band below says it
          in the card's own colour, and two banners repeating one sentence is
          just noise at the top of every archived card. */}

      {/* ── Identity band + details ──────────────────────────────────
          One card, two zones: a status-tinted band that says what this card
          IS right now, and the fields that change it. Before this the drawer
          opened on a flat strip of inputs, so the most prominent thing on
          screen was a "Description" placeholder — nothing told you whether
          the card was live, or how much was on it. */}
      <Card
        size="small"
        style={{ marginBottom: 16, borderColor: statusTheme(detail?.status).ring, overflow: 'hidden' }}
        styles={{ body: { padding: 0 } }}
      >
        <div
          style={{
            background: statusTheme(detail?.status).soft,
            borderLeft: `3px solid ${statusTheme(detail?.status).accent}`,
            borderBottom: `1px solid ${statusTheme(detail?.status).ring}`,
            padding: '9px 16px',
            display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap'
          }}
        >
          <span style={{ fontSize: 12.5, color: '#334155' }}>
            {statusTheme(detail?.status).hint}
          </span>
          <span style={{ display: 'flex', gap: 26, marginLeft: 'auto' }}>
            <Fact label="Rates" value={rates.length} />
            <Fact label="Customers" value={detail?.assignments?.length || 0}
              tone={(detail?.assignments?.length || 0) === 0 ? '#b45309' : undefined}
              hint={(detail?.assignments?.length || 0) === 0
                ? 'Assigned to nobody, so it prices nothing yet' : undefined} />
          </span>
        </div>

        <div style={{ padding: '14px 16px 0' }}>
      {/* A flex ROW with explicit widths, not `Space wrap`: Space treats every
          child as an indivisible block, so the flexible Description could not
          share the row and dropped onto a line of its own, leaving a band of
          dead space across the top of the drawer. */}
      <Form form={form} layout="vertical" disabled={readOnly}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Form.Item
            label="List name" name="listName"
            rules={[{ required: true, message: 'A name is required.' }]}
            style={{ width: 256, marginBottom: 14 }}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Effective from" name="effectiveFrom" style={{ width: 148, marginBottom: 14 }}>
            <DatePicker format={DATE_FMT} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Effective to" name="effectiveTo" style={{ width: 148, marginBottom: 14 }}>
            <DatePicker format={DATE_FMT} placeholder="open-ended" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label={<Tooltip title="Only used when two lists of the same scope overlap on the same date. Higher wins.">Priority</Tooltip>}
            name="priority"
            style={{ width: 96, marginBottom: 14 }}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="Description" name="description"
            style={{ flex: '1 1 260px', minWidth: 240, marginBottom: 14 }}
          >
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="The agreement, the approval reference…" />
          </Form.Item>
        </div>
      </Form>
        </div>
      </Card>

      <Tabs
        defaultActiveKey="ITEM"
        onChange={(k) => { if (k === 'HISTORY') loadEvents(); }}
        items={[
          ...KINDS.map((k) => ({
            key: k.key,
            label: <span>{k.label}{counts[k.key] ? <Tag style={{ marginLeft: 6 }}>{counts[k.key]}</Tag> : null}</span>,
            children: (
              <RateTab
                kind={k}
                options={options[k.key] || []}
                rates={rates}
                onChange={setRates}
                readOnly={readOnly}
                onSearchItems={searchItems}
              />
            )
          })),
          {
            key: 'ASSIGNMENTS',
            label: <span>Assignments{detail?.assignments?.length ? <Tag style={{ marginLeft: 6 }}>{detail.assignments.length}</Tag> : null}</span>,
            children: detail ? (
              <AssignmentsTab
                listId={listId}
                assignments={detail.assignments}
                readOnly={readOnly}
                onChanged={load}
              />
            ) : null
          },
          {
            key: 'HISTORY',
            label: <span><HistoryOutlined /> History</span>,
            children: (
              <Table
                size="small"
                rowKey="id"
                dataSource={events}
                pagination={pager(10)}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No changes recorded yet." /> }}
                columns={[
                  {
                    title: 'When',
                    dataIndex: 'createdAt',
                    width: 170,
                    render: (v) => (v ? dayjs(v).format('DD-MM-YYYY HH:mm') : '')
                  },
                  { title: 'Change', dataIndex: 'eventType', width: 150, render: (v) => <Tag>{v}</Tag> },
                  {
                    title: 'Detail',
                    key: 'detail',
                    render: (_v, r) => (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {r.remarks || JSON.stringify(r.newValues || {}).slice(0, 220)}
                      </Text>
                    )
                  }
                ]}
              />
            )
          }
        ]}
      />
      {loading && <div style={{ padding: 24 }}><Text type="secondary">Loading…</Text></div>}
      </div>
    </div>
  );
}
